/**
 * Token Skeleton 对齐——为 keyword matcher 的 phase 2a 提供"格式相同、内容
 * 不同的位置自动当变量"的能力。
 *
 * 这层是 isomorphic 的：纯 TS、零 Node 依赖，前端 SPA 与 Node CLI/server
 * 都能直接 import。
 *
 * 设计要点：
 * - 把字符串按 `\s+` 切成 `tokens`（实际词）+ `seps`（每两个 token 之间的
 *   空白序列），sep 的空格数/tab/换行**严格**保留——`a b` 与 `a  b` 不视为
 *   等价，避免日志格式漂移导致误命中。
 * - 在 `line` 的 token 数组上滑动 `keyword.tokens.length` 长度的窗口，
 *   要求每个相邻 token 之间的 sep 完全一致；窗口内允许若干 token 内容
 *   不同，但要求"至少一半 token 完全相等"才算骨架命中。
 * - 命中时回传 diff 位置（相对 keyword.tokens 的索引），上层据此调
 *   `classifyToken` 给每个 diff 打类型标签，再由 UI 渲染成 amber 高亮 +
 *   tooltip。
 */

/** keyword/line 的 tokenize 结果。 */
export interface Tokenized {
  /** 实际词 token，按 `\s+` 切，已 trim 整体首尾空白。 */
  tokens: string[];
  /** 每个 token 后紧跟的空白序列；长度 = `tokens.length - 1`。 */
  seps: string[];
}

/** matchSkeleton 的返回值。 */
export interface SkeletonMatch {
  hit: boolean;
  /** 命中时：line.tokens 中匹配窗口起点（token 索引）。 */
  windowStart?: number;
  /** 命中时：keyword 中与 line 不一致的 token 位置（0-based）。 */
  diffs?: number[];
}

/**
 * 把字符串切成 token + sep。
 *
 * - 整体先 trim 首尾空白（避免 `'ANR in '` 末尾产生空 token）。
 * - 按 `(\s+)` split：偶数 index 为 token、奇数 index 为 sep。
 * - 空字符串 / 纯空白 → 返回空数组。
 *
 * 偶数 index 为 token、奇数 index 为 sep 的约束由 String.split 与正则
 * 捕获组天然保证；这里的 trim 只剥掉整体两端，不动内部的多空格 / tab。
 */
export function tokenize(s: string): Tokenized {
  const trimmed = s.replace(/^\s+|\s+$/g, '');
  if (trimmed === '') return { tokens: [], seps: [] };
  const parts = trimmed.split(/(\s+)/);
  const tokens: string[] = [];
  const seps: string[] = [];
  for (let i = 0; i < parts.length; i++) {
    if (i % 2 === 0) tokens.push(parts[i]);
    else seps.push(parts[i]);
  }
  return { tokens, seps };
}

/**
 * 在 `line` 中查找 `keyword` 的骨架匹配。
 *
 * 算法：
 * 1. 双方 tokenize；keyword token 数 < 2 直接 miss（让 phase 1 字面决定）。
 * 2. keyword token 数 > line token 数 → miss。
 * 3. 滑窗 `s ∈ [0, lnT.tokens.length - kwT.tokens.length]`：
 *    - 检查窗口内每个相邻 sep 是否一致（不一致即跳过该窗口）；
 *    - 收集 token diff 位置；
 *    - 若 `fixedCount = kwT.tokens.length - diffs.length` 满足
 *      `fixedCount >= ceil(N/2)` 且 `>= 1`，**首匹配优先**返回命中。
 * 4. 全部窗口都不满足 → miss。
 *
 * @param keyword 用户配置的关键字字面量；上层应已 applyParams。
 * @param line    当前日志行的目标字符串（msg 优先，msg 空则 raw）。
 * @param ignoreCase 是否大小写不敏感。当 true 时，token 比对走
 *                   `.toLowerCase()`；sep 不参与大小写。
 */
export function matchSkeleton(
  keyword: string,
  line: string,
  ignoreCase = false,
): SkeletonMatch {
  const kwT = tokenize(keyword);
  const lnT = tokenize(line);

  const N = kwT.tokens.length;
  if (N < 2) return { hit: false };
  if (N > lnT.tokens.length) return { hit: false };

  const minFixed = Math.max(1, Math.ceil(N / 2));
  const norm = (t: string): string => (ignoreCase ? t.toLowerCase() : t);

  for (let s = 0; s + N <= lnT.tokens.length; s++) {
    // 1) sep 一致性检查（共 N-1 个 sep）
    let sepOk = true;
    for (let i = 0; i < N - 1; i++) {
      if (kwT.seps[i] !== lnT.seps[s + i]) {
        sepOk = false;
        break;
      }
    }
    if (!sepOk) continue;

    // 2) token diff 收集
    const diffs: number[] = [];
    for (let i = 0; i < N; i++) {
      if (norm(kwT.tokens[i]) !== norm(lnT.tokens[s + i])) {
        diffs.push(i);
      }
    }

    // 3) 阈值：固定 token 占过半
    if (N - diffs.length >= minFixed) {
      return { hit: true, windowStart: s, diffs };
    }
  }

  return { hit: false };
}
